// src/pages/tariff/TariffsPage.js
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  Chip, 
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Paper,
  Stack,
  Tooltip
} from '@mui/material';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { getTariffs, getCompanyTariffs, deleteTariff } from '../../api/tariffAPI';
import { getCompanies } from '../../api/companyAPI';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const TariffsPage = () => {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tariffToDelete, setTariffToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [filters, setFilters] = useState({
    companyId: '',
    enabled: '',
    tariffType: '',
    search: ''
  });

  // Fetch companies
  const { data: companies, isLoading: isLoadingCompanies, error: companiesError } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getCompanies()
  });

  // Set default company
  useEffect(() => {
    if (companies && companies.length > 0 && !filters.companyId) {
      setFilters(prev => ({
        ...prev,
        companyId: companies[0].CompanyId.toString()
      }));
    }
  }, [companies, filters.companyId]);

  // Fetch tariffs - modified to handle both company-specific and all tariffs
  const { data: tariffs, isLoading, isError, error } = useQuery({
    queryKey: ['tariffs', filters],
    queryFn: () => {
      if (filters.companyId) {
        return getCompanyTariffs(
          parseInt(filters.companyId),
          filters.enabled !== '' ? filters.enabled === 'true' : undefined,
          filters.tariffType || undefined
        );
      } else {
        // Fallback to get all tariffs if no company is selected
        const params = {};
        if (filters.enabled !== '') params.enabled = filters.enabled === 'true';
        if (filters.tariffType) params.tariff_type = filters.tariffType;
        return getTariffs(params);
      }
    },
    enabled: !!filters.companyId || !isLoadingCompanies, // Enable query if we have a company or companies finished loading
    retry: 1
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteTariff,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tariffs'] });
      if (filters.companyId) {
        queryClient.invalidateQueries({ queryKey: ['company-tariffs', parseInt(filters.companyId)] });
      }
      setSnackbar({
        open: true,
        message: 'Tariff deleted successfully',
        severity: 'success'
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error deleting tariff: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleDeleteClick = (tariff) => {
    setTariffToDelete({
      id: tariff.TariffsId,
      name: tariff.TariffsName
    });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (tariffToDelete) {
      try {
        await deleteMutation.mutateAsync(tariffToDelete.id);
      } catch (err) {
        // Error handled by mutation
      }
      setDeleteDialogOpen(false);
      setTariffToDelete(null);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Filter tariffs by search
  const filteredTariffs = tariffs && tariffs.filter(tariff => {
    if (!filters.search) return true;
    const searchTerm = filters.search.toLowerCase();
    return (
      tariff.TariffsName?.toLowerCase().includes(searchTerm) ||
      tariff.TariffsType?.toLowerCase().includes(searchTerm)
    );
  });

  const currentCompany = companies?.find(c => c.CompanyId.toString() === filters.companyId);

  if (isLoadingCompanies) {
    return <LoadingSpinner />;
  }

  if (companiesError) {
    return <ErrorAlert message="Failed to load companies. Please try again." />;
  }

  if (!companies || companies.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No companies found
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          You need to create a company first before managing tariffs.
        </Typography>
        <Button 
          component={Link} 
          to="/companies/new"
          variant="contained" 
          startIcon={<AddIcon />}
          sx={{ mt: 2 }}
        >
          Create Company
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Tariffs</Typography>
        <Button 
          component={Link} 
          to="/tariffs/new" 
          variant="contained" 
          startIcon={<AddIcon />}
        >
          Add Tariff
        </Button>
      </Box>

      {/* Filters */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <TextField
              name="search"
              label="Search Tariffs"
              variant="outlined"
              fullWidth
              value={filters.search}
              onChange={handleFilterChange}
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ height: 56 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ height: 56 }}>
              <InputLabel shrink>Company</InputLabel>
              <Select
                name="companyId"
                value={filters.companyId}
                label="Company"
                onChange={handleFilterChange}
                displayEmpty
                size="medium"
                startAdornment={<BusinessIcon sx={{ mr: 1 }} />}
                sx={{ 
                  height: 56,
                  '& .MuiSelect-select': {
                    paddingTop: '14px',
                    paddingBottom: '14px'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {companies && companies.map((company) => (
                  <MenuItem key={company.CompanyId} value={company.CompanyId.toString()}>
                    {company.CompanyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ height: 56 }}>
              <InputLabel shrink>Status</InputLabel>
              <Select
                name="enabled"
                value={filters.enabled}
                label="Status"
                onChange={handleFilterChange}
                displayEmpty
                size="medium"
                sx={{ 
                  height: 56,
                  '& .MuiSelect-select': {
                    paddingTop: '14px',
                    paddingBottom: '14px'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="true">Enabled</MenuItem>
                <MenuItem value="false">Disabled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ height: 56 }}>
              <InputLabel shrink>Type</InputLabel>
              <Select
                name="tariffType"
                value={filters.tariffType}
                label="Type"
                onChange={handleFilterChange}
                displayEmpty
                size="medium"
                sx={{ 
                  height: 56,
                  '& .MuiSelect-select': {
                    paddingTop: '14px',
                    paddingBottom: '14px'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="Fixed">Fixed</MenuItem>
                <MenuItem value="Time-based">Time-based</MenuItem>
                <MenuItem value="Energy-based">Energy-based</MenuItem>
                <MenuItem value="Power-based">Power-based</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Company info */}
      {currentCompany && (
        <Box mb={3}>
          <Typography variant="h6">
            Tariffs for: {currentCompany.CompanyName}
          </Typography>
        </Box>
      )}

      {/* Loading state */}
      {isLoading && <LoadingSpinner />}

      {/* Error state */}
      {isError && !isLoading && (
        <ErrorAlert message={error instanceof Error ? error.message : 'Failed to load tariffs'} />
      )}

      {/* No tariffs state */}
      {!isLoading && !isError && (!filteredTariffs || filteredTariffs.length === 0) && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No tariffs found
          </Typography>
          <Button 
            component={Link} 
            to="/tariffs/new"
            variant="contained" 
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
          >
            Add Your First Tariff
          </Button>
        </Box>
      )}

      {/* Tariffs Grid */}
      {!isLoading && !isError && filteredTariffs && filteredTariffs.length > 0 && (
        <Grid container spacing={3}>
          {filteredTariffs.map((tariff) => (
            <Grid item xs={12} md={6} lg={4} key={tariff.TariffsId}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" alignItems="center">
                      <AttachMoneyIcon sx={{ mr: 1 }} />
                      <Typography variant="h6" noWrap>
                        {tariff.TariffsName}
                      </Typography>
                    </Box>
                    <Chip 
                      label={tariff.TariffsEnabled ? "Enabled" : "Disabled"} 
                      color={tariff.TariffsEnabled ? "success" : "default"}
                      size="small"
                    />
                  </Box>
                  
                  {tariff.TariffsType && (
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Type: {tariff.TariffsType}
                    </Typography>
                  )}
                  
                  {tariff.TariffsPer && (
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Charged per: {tariff.TariffsPer}
                    </Typography>
                  )}
                  
                  {tariff.TariffsRateDaytime && (
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Daytime rate: ${tariff.TariffsRateDaytime}
                    </Typography>
                  )}
                </CardContent>
                
                <Box sx={{ p: 2, pt: 0 }}>
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Tooltip title="View Details">
                      <Button
                        component={Link}
                        to={`/tariffs/${tariff.TariffsId}`}
                        variant="outlined"
                        color="primary"
                        startIcon={<VisibilityIcon />}
                        size="small"
                      >
                        View
                      </Button>
                    </Tooltip>
                    
                    <Tooltip title="Edit Tariff">
                      <Button
                        component={Link}
                        to={`/tariffs/${tariff.TariffsId}/edit`}
                        variant="outlined"
                        color="info"
                        startIcon={<EditIcon />}
                        size="small"
                      >
                        Edit
                      </Button>
                    </Tooltip>
                    
                    <Tooltip title="Delete Tariff">
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteClick(tariff)}
                        size="small"
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </Stack>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Tariff</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{tariffToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TariffsPage;